import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Button,
  VStack,
  HStack,
  Text,
  useToast,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Icon,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  SimpleGrid,
  Select,
  Box,
} from '@chakra-ui/react';
import { FaTrophy, FaSortAmountDown, FaStar, FaMedal, FaChartLine } from 'react-icons/fa';
import { getStoredGames, clearLeaderboard } from '../services/leaderboardService';
import { TOTAL_ROUNDS } from '../constants';

const LeaderboardModal = ({ isOpen, onClose }) => {
  const toast = useToast();
  const [sortField, setSortField] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [timeFilter, setTimeFilter] = useState('all');
  const games = getStoredGames();

  // Filter games by time
  const filteredGames = games.filter(game => {
    const gameDate = new Date(game.date);
    const now = new Date();
    switch (timeFilter) {
      case 'today':
        return gameDate.toDateString() === now.toDateString();
      case 'week':
        return (now - gameDate) / (1000 * 60 * 60 * 24) <= 7;
      case 'month':
        return (now - gameDate) / (1000 * 60 * 60 * 24) <= 30;
      default:
        return true;
    }
  });

  // Sort games
  const sortedGames = [...filteredGames].sort((a, b) => {
    let comparison = 0;
    switch (sortField) {
      case 'score':
        comparison = b.score - a.score;
        break;
      case 'accuracy':
        comparison = (b.stats.correctGuesses / TOTAL_ROUNDS) - (a.stats.correctGuesses / TOTAL_ROUNDS);
        break;
      case 'streak':
        comparison = b.stats.bestStreak - a.stats.bestStreak;
        break;
      case 'date':
        comparison = new Date(b.date) - new Date(a.date);
        break;
      default:
        comparison = 0;
    }
    return sortOrder === 'desc' ? comparison : -comparison;
  });

  // Calculate high scores and stats
  const highScore = Math.max(...games.map(g => g.score));
  const bestStreak = Math.max(...games.map(g => g.stats.bestStreak));
  const totalGames = games.length;
  const averageScore = Math.round(games.reduce((sum, g) => sum + g.score, 0) / totalGames) || 0;

  // Add toggle sort order function
  const toggleSort = (field) => {
    if (field === sortField) {
      setSortOrder(current => current === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay backdropFilter="blur(5px)" />
      <ModalContent>
        <ModalHeader>
          <HStack>
            <Icon as={FaTrophy} color="yellow.400" boxSize={6} />
            <Text>Leaderboard</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          {games.length > 0 ? (
            <VStack spacing={6} align="stretch">
              {/* Stats Overview */}
              <SimpleGrid columns={4} spacing={4}>
                <Stat bg="yellow.50" p={3} borderRadius="lg">
                  <StatLabel>High Score</StatLabel>
                  <StatNumber color="yellow.500">{highScore}</StatNumber>
                  <StatHelpText><Icon as={FaTrophy} /></StatHelpText>
                </Stat>
                <Stat bg="green.50" p={3} borderRadius="lg">
                  <StatLabel>Best Streak</StatLabel>
                  <StatNumber color="green.500">{bestStreak}</StatNumber>
                  <StatHelpText><Icon as={FaChartLine} /></StatHelpText>
                </Stat>
                <Stat bg="blue.50" p={3} borderRadius="lg">
                  <StatLabel>Games Played</StatLabel>
                  <StatNumber color="blue.500">{totalGames}</StatNumber>
                  <StatHelpText><Icon as={FaStar} /></StatHelpText>
                </Stat>
                <Stat bg="purple.50" p={3} borderRadius="lg">
                  <StatLabel>Avg Score</StatLabel>
                  <StatNumber color="purple.500">{averageScore}</StatNumber>
                  <StatHelpText><Icon as={FaMedal} /></StatHelpText>
                </Stat>
              </SimpleGrid>

              {/* Filters */}
              <HStack spacing={4}>
                <Select 
                  value={timeFilter}
                  onChange={(e) => setTimeFilter(e.target.value)}
                  size="sm"
                  w="200px"
                >
                  <option value="all">All Time</option>
                  <option value="today">Today</option>
                  <option value="week">This Week</option>
                  <option value="month">This Month</option>
                </Select>
                <Menu>
                  <MenuButton as={Button} rightIcon={<FaSortAmountDown />} size="sm">
                    Sort By
                  </MenuButton>
                  <MenuList>
                    <MenuItem onClick={() => toggleSort('date')}>
                      Date {sortField === 'date' && (sortOrder === 'desc' ? '↓' : '↑')}
                    </MenuItem>
                    <MenuItem onClick={() => toggleSort('score')}>
                      Score {sortField === 'score' && (sortOrder === 'desc' ? '↓' : '↑')}
                    </MenuItem>
                    <MenuItem onClick={() => toggleSort('accuracy')}>
                      Accuracy {sortField === 'accuracy' && (sortOrder === 'desc' ? '↓' : '↑')}
                    </MenuItem>
                    <MenuItem onClick={() => toggleSort('streak')}>
                      Streak {sortField === 'streak' && (sortOrder === 'desc' ? '↓' : '↑')}
                    </MenuItem>
                  </MenuList>
                </Menu>
              </HStack>

              {/* Games Table */}
              <Box overflowX="auto">
                <Table variant="simple">
                  <Thead>
                    <Tr>
                      <Th>Rank</Th>
                      <Th>Date</Th>
                      <Th>Categories</Th>
                      <Th isNumeric>Score</Th>
                      <Th>Stats</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {sortedGames.map((game, index) => (
                      <Tr key={game.id}>
                        <Td>
                          {index + 1}
                          {index < 3 && (
                            <Icon 
                              as={FaMedal} 
                              color={index === 0 ? "yellow.400" : index === 1 ? "gray.400" : "orange.400"}
                              ml={2}
                            />
                          )}
                        </Td>
                        <Td>{new Date(game.date).toLocaleDateString()}</Td>
                        <Td>
                          <HStack spacing={1} flexWrap="wrap">
                            {game.categories.map((cat) => (
                              <Badge key={cat} colorScheme="blue" size="sm">
                                {cat}
                              </Badge>
                            ))}
                          </HStack>
                        </Td>
                        <Td isNumeric fontWeight="bold" color="green.500">
                          {game.score}
                        </Td>
                        <Td>
                          <VStack align="start" spacing={0} fontSize="sm">
                            <Text>
                              Accuracy: {((game.stats.correctGuesses / TOTAL_ROUNDS) * 100).toFixed(1)}%
                            </Text>
                            <Text>
                              Streak: {game.stats.bestStreak}
                            </Text>
                            <Text>
                              Hints: {game.hintsUsed}
                            </Text>
                          </VStack>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            </VStack>
          ) : (
            <VStack spacing={4} py={4}>
              <Text>No games played yet!</Text>
            </VStack>
          )}
          
          {games.length > 0 && (
            <Button
              colorScheme="red"
              variant="outline"
              size="sm"
              mt={4}
              onClick={() => {
                clearLeaderboard();
                toast({
                  title: "Leaderboard cleared",
                  status: "success",
                  duration: 2000,
                });
                onClose();
              }}
            >
              Clear History
            </Button>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default LeaderboardModal; 